import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BackupService } from "../../application/services/backupService";

const backupService = new BackupService();

export const backupKeys = {
  all: ["backup"],
  vaults: () => [...backupKeys.all, "vaults"],
  byVmId: (vmId) => [...backupKeys.all, "byVmId", vmId],
};

export const useGetAllBackupVaultsWithItems = (options = {}) => {
  return useQuery({
    queryKey: backupKeys.vaults(),
    queryFn: async () => {
      const result = await backupService.getAllBackupVaultsWithItems();
      if (!result.success) {
        throw new Error(result.message);
      }
      return result.data;
    },
    staleTime: options.staleTime ?? 30 * 60 * 1000,
    gcTime: options.gcTime ?? 60 * 60 * 1000,
    refetchOnMount: options.refetchOnMount ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options.refetchOnReconnect ?? false,
    retry: options.retry ?? 1,
    enabled: options.enabled ?? true,
    ...options,
  });
};

export const useGetBackupByVmId = (vmId, options = {}) => {
  return useQuery({
    queryKey: backupKeys.byVmId(vmId),
    queryFn: async () => {
      const result = await backupService.getBackupByVmId(vmId);
      if (!result.success) {
        throw new Error(result.message);
      }
      return result.data;
    },
    enabled: !!vmId && (options.enabled ?? true),
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    ...options,
  });
};

export const useRefreshBackupVaults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await backupService.getAllBackupVaultsWithItems();
      if (!result.success) throw new Error(result.message);
      return result.data;
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(backupKeys.vaults(), newData);
    },
  });
};

export const useBackup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backupHistory, setBackupHistory] = useState(null);

  const getVmBackupHistory = async (vmId, startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const result = await backupService.getVmBackupHistory(vmId, startDate, endDate);
      if (result.success) {
        setBackupHistory(result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    getVmBackupHistory,
    backupHistory,
    loading,
    error,
  };
};